import type { ReportAnswer } from '@fieldops/shared'
import { apiOrigin, currentBearerToken } from './trpc.js'

/**
 * Sending a recording to `POST /dictation`.
 *
 * Multipart over `fetch` for the same reason `uploadFile` is: tRPC serialises its
 * input as JSON, and base64 audio is a third more bytes over the connection that
 * least affords them.
 *
 * The interesting part is the return type. A refusal from this endpoint is
 * usually *not* an error the technician needs to read — no key configured, the
 * daily cap reached, the provider unreachable — and in every one of those cases
 * the right thing is to sort the words on the phone instead. So the fallback is a
 * result, not an exception, and it carries the transcript when the server got far
 * enough to have one. Only the answers that no engine would fix — not signed in,
 * not allowed, no such report — throw.
 */

export type CloudSuggestion = {
  fieldKey: string
  label: string
  value: ReportAnswer
  source: 'heard'
  reason: string
  heard: string
}

export type DictationOutcome =
  | {
      engine: 'cloud'
      runId: string
      transcript: string
      model: string
      suggestions: CloudSuggestion[]
      /** Field keys the pass got nothing for, named rather than dropped. */
      unmatched: string[]
    }
  | {
      engine: 'device'
      /** Why the cloud pass did not happen, in the provider's own words. */
      reason: string
      /** Present when transcription worked and only the mapping failed. */
      transcript: string | null
    }

/** Statuses that mean "this request was wrong", not "try it on the phone". */
const FATAL = new Set([401, 403, 404, 409, 415])

export async function sendDictation(
  audio: Blob,
  options: {
    responseId: string
    sectionKey?: string | null
    /** What the recorder measured, so the server can refuse a long clip early. */
    seconds?: number | null
    filename?: string
  },
): Promise<DictationOutcome> {
  const body = new FormData()
  body.append('responseId', options.responseId)
  if (options.sectionKey) body.append('sectionKey', options.sectionKey)
  if (options.seconds) body.append('seconds', String(Math.round(options.seconds)))
  body.append('file', audio, options.filename ?? 'dictation.webm')

  const token = currentBearerToken()

  let response: Response

  try {
    response = await fetch(`${apiOrigin}/dictation`, {
      method: 'POST',
      credentials: 'include',
      ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
      body,
    })
  } catch {
    // No signal at all, which is the case this whole fallback exists for.
    return { engine: 'device', reason: 'There is no connection, so this stayed on the phone.', transcript: null }
  }

  const payload = (await response.json().catch(() => null)) as
    | (Partial<Extract<DictationOutcome, { engine: 'cloud' }>> & {
        error?: string
        fallback?: string
        transcript?: string
      })
    | null

  if (!response.ok) {
    const reason = payload?.error ?? `That recording did not go through (${response.status}).`

    if (FATAL.has(response.status)) throw new Error(reason)

    return { engine: 'device', reason, transcript: payload?.transcript ?? null }
  }

  if (!payload?.runId || typeof payload.transcript !== 'string') {
    return { engine: 'device', reason: 'The reply from the server made no sense.', transcript: null }
  }

  return {
    engine: 'cloud',
    runId: payload.runId,
    transcript: payload.transcript,
    model: payload.model ?? '',
    suggestions: payload.suggestions ?? [],
    unmatched: payload.unmatched ?? [],
  }
}
